import { Component, lazy, Suspense, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useSettings } from '../../settings/SettingsContext';
import { motionAllowed } from '../scene/programs';
import { screenOf } from '../screen';
import type { GradientLook } from './GradientScene';

const GradientScene = lazy(() => import('./GradientScene'));

export const LOOKS: Partial<Record<string, GradientLook>> = {
  glass: {
    type: 'plane',
    color1: '#6d83f2',
    color2: '#9fd8f7',
    color3: '#c7b8f5',
    uSpeed: 0.12,
    uStrength: 2.6,
    uDensity: 1.1,
    uFrequency: 5.5,
    uAmplitude: 1,
    brightness: 1.15,
    cAzimuthAngle: 180,
    cPolarAngle: 90,
    cDistance: 3.4,
    positionX: -1.2,
    positionY: 0,
    positionZ: 0,
    rotationX: 0,
    rotationY: 10,
    rotationZ: 50,
  },
  silk: {
    type: 'waterPlane',
    color1: '#f2c4ad',
    color2: '#f6e6d8',
    color3: '#d9a3ae',
    uSpeed: 0.08,
    uStrength: 1.6,
    uDensity: 1.4,
    uFrequency: 4,
    uAmplitude: 0,
    brightness: 1.2,
    cAzimuthAngle: 170,
    cPolarAngle: 70,
    cDistance: 4.4,
    positionX: 0,
    positionY: 0.9,
    positionZ: -0.3,
    rotationX: 45,
    rotationY: 0,
    rotationZ: 0,
  },
  chrome: {
    type: 'sphere',
    color1: '#3d4452',
    color2: '#8b95a7',
    color3: '#1d2129',
    uSpeed: 0.1,
    uStrength: 0.4,
    uDensity: 0.9,
    uFrequency: 5.5,
    uAmplitude: 3.2,
    brightness: 1.1,
    reflection: 0.5,
    cAzimuthAngle: 250,
    cPolarAngle: 140,
    cDistance: 1.5,
    cameraZoom: 12.5,
    positionX: 0,
    positionY: 0,
    positionZ: 0,
    rotationX: 0,
    rotationY: 0,
    rotationZ: 140,
  },
};

class Quiet extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() { return this.state.failed ? null : this.props.children; }
}

export default function Backdrop() {
  const { settings } = useSettings();
  const { pathname } = useLocation();
  const look = screenOf(pathname) === 'game' ? undefined : LOOKS[settings.design];
  useEffect(() => {
    if (!look) return;
    document.documentElement.setAttribute('data-backdrop', '');
    return () => document.documentElement.removeAttribute('data-backdrop');
  }, [!!look]);
  if (!look) return null;
  return (
    <div className="lq-backdrop" aria-hidden="true">
      <Quiet>
        <Suspense fallback={null}>
          <GradientScene look={look} moving={motionAllowed(settings.reduceMotion)} />
        </Suspense>
      </Quiet>
    </div>
  );
}
